import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import {
    Activity,
    Search,
    Filter,
    Calendar,
    ChevronLeft,
    ChevronRight,
    UserPlus,
    UserMinus,
    FileEdit,
    FilePlus,
    CheckCircle2,
    PlusCircle
} from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useAuth } from '../../contexts/AuthContext';

interface ActivityLog {
    id: string;
    action_type: string;
    entity_type: string;
    entity_id: string;
    title: string;
    description: string;
    created_at: string;
    metadata: any;
    created_by: string;
}

const OperationsLogsPage: React.FC = () => {
    const { user } = useAuth();
    const [logs, setLogs] = useState<ActivityLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const ITEMS_PER_PAGE = 20;

    useEffect(() => {
        fetchLogs();
    }, [page, searchTerm]);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            let query = supabase
                .from('activity_logs')
                .select('*', { count: 'exact' })
                .eq('branch_id', user?.branchId);

            if (searchTerm) {
                query = query.or(`title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`);
            }

            const { data, count, error } = await query
                .order('created_at', { ascending: false })
                .range((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE - 1);

            if (error) throw error;

            setLogs(data || []);
            setTotalPages(Math.ceil((count || 0) / ITEMS_PER_PAGE));
        } catch (error) {
            console.error('Error fetching logs:', error);
        } finally {
            setLoading(false);
        }
    };

    const getIcon = (type: string) => {
        switch (type) {
            case 'new_student': return <UserPlus className="w-5 h-5 text-orange-600" />;
            case 'delete_student': return <UserMinus className="w-5 h-5 text-red-600" />;
            case 'new_application': return <PlusCircle className="w-5 h-5 text-blue-600" />;
            case 'update_application': return <FileEdit className="w-5 h-5 text-indigo-600" />;
            case 'new_task': return <FilePlus className="w-5 h-5 text-emerald-600" />;
            case 'completed_task': return <CheckCircle2 className="w-5 h-5 text-green-600" />;
            default: return <Activity className="w-5 h-5 text-gray-600" />;
        }
    };

    const getBgColor = (type: string) => {
        switch (type) {
            case 'new_student': return 'bg-orange-50 dark:bg-orange-900/20';
            case 'delete_student': return 'bg-red-50 dark:bg-red-900/20';
            case 'new_application': return 'bg-blue-50 dark:bg-blue-900/20';
            case 'update_application': return 'bg-indigo-50 dark:bg-indigo-900/20';
            case 'new_task': return 'bg-emerald-50 dark:bg-emerald-900/20';
            case 'completed_task': return 'bg-green-50 dark:bg-green-900/20';
            default: return 'bg-gray-50 dark:bg-gray-900/20';
        }
    };

    return (
        <div className="p-6 space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <Activity className="w-8 h-8 text-blue-600" />
                        İşlem Geçmişi
                    </h1>
                    <p className="text-gray-500 dark:text-gray-400 mt-1">
                        Sistemde yapılan işlemlerin detaylı kayıtları
                    </p>
                </div>
                <div className="flex items-center gap-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="İşlem ara..."
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                            className="pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-500 dark:text-gray-400 text-xs uppercase font-semibold">
                            <tr>
                                <th className="px-6 py-4">İşlem Detayı</th>
                                <th className="px-6 py-4">Kategori</th>
                                <th className="px-6 py-4">Tarih</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                            {loading ? (
                                <tr>
                                    <td colSpan={3} className="px-6 py-8 text-center text-gray-500">
                                        Yükleniyor...
                                    </td>
                                </tr>
                            ) : logs.length === 0 ? (
                                <tr>
                                    <td colSpan={3} className="px-6 py-8 text-center text-gray-500">
                                        Kayıt bulunamadı.
                                    </td>
                                </tr>
                            ) : (
                                logs.map((log) => (
                                    <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-4">
                                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getBgColor(log.action_type)}`}>
                                                    {getIcon(log.action_type)}
                                                </div>
                                                <div>
                                                    <div className="font-medium text-gray-900 dark:text-white">
                                                        {log.title}
                                                    </div>
                                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                                        {log.description}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${log.entity_type === 'student' ? 'bg-orange-50 text-orange-700 border-orange-100' :
                                                    log.entity_type === 'application' ? 'bg-blue-50 text-blue-700 border-blue-100' :
                                                        log.entity_type === 'task' ? 'bg-green-50 text-green-700 border-green-100' :
                                                            'bg-gray-50 text-gray-700 border-gray-100'
                                                }`}>
                                                {log.action_type.split('_').join(' ').toUpperCase()}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                                            {format(new Date(log.created_at), 'dd MMM yyyy HH:mm', { locale: tr })}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                        Toplam {totalPages} sayfa
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <span className="text-sm font-medium px-2">{page}</span>
                        <button
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OperationsLogsPage;
